// File: webelieve-frontend/src/features/intake/components/LeadDetailView.tsx
// EF-2.3: Lead detail view with 6 tabs — Overview, Documents, AI Synopsis, Activity, Notes, Communication

import React, { useState, useEffect, useCallback } from 'react';
import {
  intakeCrmApi,
  type LeadDetail,
  type LeadDocument,
  type LeadActivityEvent,
  type IntakeDocumentTemplateOption,
  type LeadProfile,
  type RecordRetainerPaymentRequest,
  type RequestRetainerRequest,
  type CourtRegistryCourt,
  type CourtRegistryState,
} from '../api/intakeCrmApi';
import type { LeadMatterPlan } from '../lib/leadIntelligence';
import IntakeChecklist from './IntakeChecklist';
import RetainerDeskModal from './RetainerDeskModal';
import ScheduleConsultationModal from './ScheduleConsultationModal';
import {
  ensureLeadCaseModules,
  LEAD_CASE_MODULE_DEFINITION_MAP,
  resolveLeadCaseModuleKeys,
  sanitizeLeadCaseModules,
  type LeadCaseModuleFieldValue,
  type LeadCaseModules,
} from '../lib/leadCaseModules';

// --- Tab types ---

type TabKey = 'overview' | 'documents' | 'synopsis' | 'activity' | 'notes' | 'communication';

interface TabConfig {
  key: TabKey;
  label: string;
  icon: React.ReactNode;
}

const TABS: TabConfig[] = [
  {
    key: 'overview',
    label: 'Overview',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
  },
  {
    key: 'documents',
    label: 'Documents',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    key: 'synopsis',
    label: 'AI Synopsis',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
  },
  {
    key: 'activity',
    label: 'Activity',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    key: 'notes',
    label: 'Notes',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
  },
  {
    key: 'communication',
    label: 'Contact Log',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    ),
  },
];

// --- Props ---

interface LeadDetailViewProps {
  clientId: string;
  onBack: () => void;
  onConvertRequest: (clientId: string) => void;
}

const LeadDetailView: React.FC<LeadDetailViewProps> = ({
  clientId,
  onBack,
  onConvertRequest,
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [lead, setLead] = useState<LeadDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Note/contact form state
  const [noteText, setNoteText] = useState('');
  const [contactMethod, setContactMethod] = useState('phone');
  const [contactNotes, setContactNotes] = useState('');
  const [contactOutcome, setContactOutcome] = useState('');
  const [saving, setSaving] = useState(false);

  const loadLead = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const detail = await intakeCrmApi.getLeadDetail(clientId);
      setLead(detail);
    } catch (err) {
      console.error('[LeadDetailView] Failed to load lead:', err);
      setError(err instanceof Error ? err.message : 'Failed to load lead details');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadLead();
  }, [loadLead]);

  useEffect(() => {
    if (lead?.operationalProjection?.conflict_check_posture?.external_enrichment_status !== 'running') {
      return;
    }

    const interval = window.setInterval(async () => {
      try {
        const detail = await intakeCrmApi.getLeadDetail(clientId);
        setLead(detail);
      } catch (err) {
        console.error('[LeadDetailView] Failed to poll external enrichment progress:', err);
      }
    }, 2000);

    return () => {
      window.clearInterval(interval);
    };
  }, [clientId, lead?.operationalProjection?.conflict_check_posture?.external_enrichment_status]);

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await intakeCrmApi.addNote(clientId, noteText.trim());
      setNoteText('');
      await loadLead(); // Refresh to show new note
    } catch (err) {
      console.error('[LeadDetailView] Failed to add note:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleChecklistToggle = useCallback(async (itemId: string, checked: boolean) => {
    try {
      await intakeCrmApi.updateChecklist(clientId, itemId, checked);
      await loadLead();
    } catch (err) {
      console.error('[LeadDetailView] Failed to update checklist:', err);
    }
  }, [clientId, loadLead]);

  const handleRunConflictCheck = useCallback(async () => {
    try {
      await intakeCrmApi.runConflictCheck(clientId);
      await loadLead();
    } catch (err) {
      console.error('[LeadDetailView] Failed to run conflict check:', err);
    }
  }, [clientId, loadLead]);

  const handleRequestConflictReview = useCallback(async (note?: string) => {
    try {
      await intakeCrmApi.requestConflictReview(clientId, note);
      await loadLead();
    } catch (err) {
      console.error('[LeadDetailView] Failed to request conflict review:', err);
    }
  }, [clientId, loadLead]);

  const handleRecordConflictDisposition = useCallback(async (
    disposition: 'clear' | 'waiver_required' | 'screen_required' | 'declined' | 'blocked',
    note?: string,
  ) => {
    try {
      await intakeCrmApi.recordConflictDisposition(clientId, disposition, note);
      await loadLead();
    } catch (err) {
      console.error('[LeadDetailView] Failed to record conflict disposition:', err);
    }
  }, [clientId, loadLead]);

  const handleRunExternalConflictEnrichment = useCallback(async () => {
    try {
      await intakeCrmApi.runExternalConflictEnrichment(clientId);
      await loadLead();
    } catch (err) {
      console.error('[LeadDetailView] Failed to run external conflict enrichment:', err);
    }
  }, [clientId, loadLead]);

  const handleScheduleConsultation = useCallback(async (payload: {
    start_time: string;
    end_time?: string;
    duration_minutes?: number;
    meeting_mode?: 'phone' | 'video' | 'in_person' | 'court' | 'other';
    location?: string;
    notes?: string;
    include_client_as_attendee?: boolean;
    create_prep_task?: boolean;
  }) => {
    try {
      await intakeCrmApi.scheduleConsultation(clientId, payload);
      await loadLead();
    } catch (err) {
      console.error('[LeadDetailView] Failed to schedule consultation:', err);
      throw err;
    }
  }, [clientId, loadLead]);

  const handleRequestRetainer = useCallback(async (payload: RequestRetainerRequest) => {
    try {
      await intakeCrmApi.requestRetainer(clientId, payload);
      await loadLead();
    } catch (err) {
      console.error('[LeadDetailView] Failed to request retainer:', err);
      throw err;
    }
  }, [clientId, loadLead]);

  const handleRecordRetainerPayment = useCallback(async (payload: RecordRetainerPaymentRequest) => {
    try {
      await intakeCrmApi.recordRetainerPayment(clientId, payload);
      await loadLead();
    } catch (err) {
      console.error('[LeadDetailView] Failed to record retainer payment:', err);
      throw err;
    }
  }, [clientId, loadLead]);

  const handleRecordContact = async () => {
    if (!contactMethod) return;
    setSaving(true);
    try {
      await intakeCrmApi.recordContact(
        clientId,
        contactMethod,
        contactNotes.trim() || undefined,
        contactOutcome.trim() || undefined
      );
      setContactNotes('');
      setContactOutcome('');
      await loadLead();
    } catch (err) {
      console.error('[LeadDetailView] Failed to record contact:', err);
    } finally {
      setSaving(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading lead details...</span>
      </div>
    );
  }

  // Error
  if (error || !lead) {
    return (
      <div className="p-6">
        <button onClick={onBack} className="text-sm text-lex-accent hover:underline mb-4">
          &larr; Back to Pipeline
        </button>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error || 'Lead not found'}</p>
        </div>
      </div>
    );
  }

  const isConvertible = ['lead', 'qualified'].includes(lead.status?.toLowerCase());

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {lead.clientName}
              </h2>
              <div className="flex items-center gap-2 mt-0.5">
                <StatusBadge status={lead.status} />
                {lead.practiceArea?.split(',').map((area) => area.trim()).filter(Boolean).map((area) => (
                  <span key={area} className="px-2 py-0.5 text-xs font-medium rounded bg-lex-accent-soft text-lex-accent">
                    {area}
                  </span>
                ))}
                {lead.source && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    via {lead.source}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isConvertible && (
              <button
                onClick={() => onConvertRequest(clientId)}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                Convert to Client
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex-shrink-0 flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.key
                ? 'border-b-2 border-lex-accent text-lex-accent bg-white dark:bg-gray-800'
                : 'text-gray-600 dark:text-gray-300 hover:text-lex-accent hover:bg-white dark:hover:bg-gray-800'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'overview' && (
          <OverviewPanel
            lead={lead}
            onChecklistToggle={handleChecklistToggle}
            onRunConflictCheck={handleRunConflictCheck}
            onRequestConflictReview={handleRequestConflictReview}
            onRecordConflictDisposition={handleRecordConflictDisposition}
            onRunExternalConflictEnrichment={handleRunExternalConflictEnrichment}
            onScheduleConsultation={handleScheduleConsultation}
            onRequestRetainer={handleRequestRetainer}
            onRecordRetainerPayment={handleRecordRetainerPayment}
            onLeadRefresh={loadLead}
          />
        )}
        {activeTab === 'documents' && <DocumentsPanel documents={lead.documents} clientId={clientId} />}
        {activeTab === 'synopsis' && <SynopsisPanel synopsis={lead.synopsis} lead={lead} />}
        {activeTab === 'activity' && <ActivityPanel activity={lead.activity} />}
        {activeTab === 'notes' && (
          <NotesPanel
            lead={lead}
            noteText={noteText}
            onNoteTextChange={setNoteText}
            onSubmit={handleAddNote}
            saving={saving}
          />
        )}
        {activeTab === 'communication' && (
          <CommunicationPanel
            lead={lead}
            contactMethod={contactMethod}
            contactNotes={contactNotes}
            contactOutcome={contactOutcome}
            onMethodChange={setContactMethod}
            onNotesChange={setContactNotes}
            onOutcomeChange={setContactOutcome}
            onSubmit={handleRecordContact}
            saving={saving}
          />
        )}
      </div>
    </div>
  );
};

// --- Sub-components ---

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    lead: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', label: 'Lead' },
    qualified: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', label: 'Qualified' },
    converted: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', label: 'Converted' },
    rejected: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', label: 'Rejected' },
    inactive: { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-600 dark:text-gray-400', label: 'Inactive' },
  };
  const c = config[status?.toLowerCase()] || config.lead;
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

// --- Overview Tab ---

interface LeadProfileFormState {
  preferredName: string;
  email: string;
  phone: string;
  alternatePhone: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  preferredContactMethod: string;
  preferredContactTime: string;
  language: string;
  interpreterNeeded: boolean;
  dateOfBirth: string;
  maritalStatus: string;
  educationLevel: string;
  numberOfDependents: string;
  numberOfKids: string;
  employed: '' | 'yes' | 'no';
  employerName: string;
  jobTitle: string;
  practiceAreasText: string;
  matterSummary: string;
  stateCode: string;
  jurisdictionCode: string;
  incidentDate: string;
  incidentLocation: string;
  opposingPartyNames: string;
  currentOrPriorAttorney: string;
  spouseOrPartnerName: string;
  caseModules: LeadCaseModules;
  matterPlans: LeadMatterPlan[];
}

function createProfileFormState(lead: LeadDetail): LeadProfileFormState {
  const profile = lead.leadProfile || {};
  const practiceAreas = (profile.practiceAreas && profile.practiceAreas.length > 0
    ? profile.practiceAreas
    : (profile.practiceArea || lead.practiceArea || '').split(','))
    .map(item => item.trim())
    .filter(Boolean);
  return {
    preferredName: profile.preferredName || '',
    email: profile.primaryEmail || lead.email || '',
    phone: profile.primaryPhone || lead.phone || '',
    alternatePhone: profile.alternatePhone || '',
    street1: profile.homeAddress?.street1 || '',
    street2: profile.homeAddress?.street2 || '',
    city: profile.homeAddress?.city || '',
    state: profile.homeAddress?.state || '',
    postalCode: profile.homeAddress?.postalCode || '',
    country: profile.homeAddress?.country || '',
    preferredContactMethod: profile.preferredContactMethod || '',
    preferredContactTime: profile.preferredContactTime || '',
    language: profile.language || '',
    interpreterNeeded: profile.interpreterNeeded || false,
    dateOfBirth: profile.dateOfBirth || '',
    maritalStatus: profile.maritalStatus || '',
    educationLevel: profile.educationLevel || '',
    numberOfDependents: profile.numberOfDependents != null ? String(profile.numberOfDependents) : '',
    numberOfKids: profile.numberOfKids != null ? String(profile.numberOfKids) : '',
    employed: typeof profile.employed === 'boolean' ? (profile.employed ? 'yes' : 'no') : '',
    employerName: profile.employerName || '',
    jobTitle: profile.jobTitle || '',
    practiceAreasText: practiceAreas.join(', '),
    matterSummary: profile.shortSummary || lead.intakeNotes || '',
    stateCode: profile.stateCode || '',
    jurisdictionCode: profile.jurisdictionCode || '',
    incidentDate: profile.incidentDate || '',
    incidentLocation: profile.incidentLocation || '',
    opposingPartyNames: profile.opposingPartyNames || '',
    currentOrPriorAttorney: profile.currentOrPriorAttorney || '',
    spouseOrPartnerName: profile.spouseOrPartnerName || '',
    caseModules: profile.caseModules || {},
    matterPlans: profile.matterPlans && profile.matterPlans.length > 0
      ? profile.matterPlans
      : practiceAreas.map((practiceArea) => ({
          practiceArea,
          title: practiceArea,
          summary: profile.shortSummary || lead.intakeNotes || '',
          stateCode: profile.stateCode || '',
          jurisdictionCode: profile.jurisdictionCode || '',
          jurisdictionCourtName: profile.jurisdictionCourtName,
          jurisdiction: profile.jurisdiction,
          incidentDate: profile.incidentDate || '',
          incidentLocation: profile.incidentLocation || '',
        })),
  };
}

function OverviewPanel({
  lead,
  onChecklistToggle,
  onRunConflictCheck,
  onRequestConflictReview,
  onRecordConflictDisposition,
  onRunExternalConflictEnrichment,
  onScheduleConsultation,
  onRequestRetainer,
  onRecordRetainerPayment,
  onLeadRefresh,
}: {
  lead: LeadDetail;
  onChecklistToggle: (itemId: string, checked: boolean) => void;
  onRunConflictCheck: () => Promise<void>;
  onRequestConflictReview: (note?: string) => Promise<void>;
  onRecordConflictDisposition: (disposition: 'clear' | 'waiver_required' | 'screen_required' | 'declined' | 'blocked', note?: string) => Promise<void>;
  onRunExternalConflictEnrichment: () => Promise<void>;
  onScheduleConsultation: (payload: {
    start_time: string;
    end_time?: string;
    duration_minutes?: number;
    meeting_mode?: 'phone' | 'video' | 'in_person' | 'court' | 'other';
    location?: string;
    notes?: string;
    include_client_as_attendee?: boolean;
    create_prep_task?: boolean;
  }) => Promise<void>;
  onRequestRetainer: (payload: RequestRetainerRequest) => Promise<void>;
  onRecordRetainerPayment: (payload: RecordRetainerPaymentRequest) => Promise<void>;
  onLeadRefresh: () => Promise<void>;
}) {
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState<LeadProfileFormState>(() => createProfileFormState(lead));
  const [jurisdictionStates, setJurisdictionStates] = useState<CourtRegistryState[]>([]);
  const [jurisdictionCourts, setJurisdictionCourts] = useState<CourtRegistryCourt[]>([]);
  const [matterPlanCourtsByState, setMatterPlanCourtsByState] = useState<Record<string, CourtRegistryCourt[]>>({});
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [consultationModalOpen, setConsultationModalOpen] = useState(false);
  const [retainerModalOpen, setRetainerModalOpen] = useState(false);
  const [retainerModalMode, setRetainerModalMode] = useState<'request' | 'payment'>('request');

  useEffect(() => {
    setProfileForm(createProfileFormState(lead));
  }, [lead]);

  const updateProfileField = <K extends keyof LeadProfileFormState>(field: K, value: LeadProfileFormState[K]) => {
    setProfileForm(prev => ({ ...prev, [field]: value }));
  };

  const updateCaseModuleField = (moduleKey: keyof LeadCaseModules, fieldKey: string, value: LeadCaseModuleFieldValue) => {
    setProfileForm(prev => ({
      ...prev,
      caseModules: {
        ...prev.caseModules,
        [moduleKey]: {
          ...(prev.caseModules[moduleKey] || {}),
          [fieldKey]: value,
        },
      },
    }));
  };

  const updateMatterPlan = (practiceArea: string, patch: Partial<LeadMatterPlan>) => {
    setProfileForm((prev) => ({
      ...prev,
      matterPlans: prev.matterPlans.map((plan) => (
        plan.practiceArea === practiceArea
          ? {
              ...plan,
              ...patch,
            }
          : plan
      )),
    }));
  };

  const parseOptionalNumber = (value: string): number | undefined => {
    if (!value.trim()) return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const parsePracticeAreas = (value: string): string[] =>
    Array.from(new Set(value.split(',').map(part => part.trim()).filter(Boolean)));

  const ensureMatterPlans = (practiceAreas: string[], existingPlans: LeadMatterPlan[], defaults: LeadProfileFormState): LeadMatterPlan[] =>
    practiceAreas.map((practiceArea) => {
      const existing = existingPlans.find((plan) => plan.practiceArea === practiceArea);
      return {
        practiceArea,
        title: existing?.title || practiceArea,
        summary: existing?.summary || defaults.matterSummary || '',
        stateCode: existing?.stateCode || defaults.stateCode || '',
        jurisdictionCode: existing?.jurisdictionCode || defaults.jurisdictionCode || '',
        jurisdictionCourtName: existing?.jurisdictionCourtName || undefined,
        jurisdiction: existing?.jurisdiction || undefined,
        incidentDate: existing?.incidentDate || defaults.incidentDate || '',
        incidentLocation: existing?.incidentLocation || defaults.incidentLocation || '',
      };
    });

  const selectedModuleKeys = resolveLeadCaseModuleKeys(parsePracticeAreas(profileForm.practiceAreasText));

  useEffect(() => {
    setProfileForm(prev => ({
      ...prev,
      caseModules: ensureLeadCaseModules(selectedModuleKeys, prev.caseModules),
      matterPlans: ensureMatterPlans(parsePracticeAreas(prev.practiceAreasText), prev.matterPlans, prev),
    }));
  }, [profileForm.practiceAreasText]);

  useEffect(() => {
    let cancelled = false;
    intakeCrmApi.listJurisdictionStates()
      .then((items) => {
        if (!cancelled) setJurisdictionStates(items);
      })
      .catch(() => {
        if (!cancelled) setJurisdictionStates([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!profileForm.stateCode) {
      setJurisdictionCourts([]);
      return;
    }
    let cancelled = false;
    intakeCrmApi.listCourtsByState(profileForm.stateCode)
      .then((items) => {
        if (!cancelled) setJurisdictionCourts(items);
      })
      .catch(() => {
        if (!cancelled) setJurisdictionCourts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [profileForm.stateCode]);

  useEffect(() => {
    const stateCodes = Array.from(new Set(profileForm.matterPlans.map((plan) => plan.stateCode).filter(Boolean))) as string[];
    const missingStateCodes = stateCodes.filter((stateCode) => !matterPlanCourtsByState[stateCode]);
    if (missingStateCodes.length === 0) return;

    let cancelled = false;
    Promise.all(missingStateCodes.map(async (stateCode) => ({
      stateCode,
      courts: await intakeCrmApi.listCourtsByState(stateCode),
    })))
      .then((results) => {
        if (cancelled) return;
        setMatterPlanCourtsByState((prev) => {
          const next = { ...prev };
          results.forEach(({ stateCode, courts: stateCourts }) => {
            next[stateCode] = stateCourts;
          });
          return next;
        });
      })
      .catch(() => {
        if (!cancelled) {
          setMatterPlanCourtsByState((prev) => ({ ...prev }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [matterPlanCourtsByState, profileForm.matterPlans]);

  const handleProfileSave = async () => {
    setProfileSaving(true);
    setProfileError(null);
    try {
      const practiceAreas = parsePracticeAreas(profileForm.practiceAreasText);
      const moduleKeys = resolveLeadCaseModuleKeys(practiceAreas);
      const selectedCourt = jurisdictionCourts.find((court) => court.jurisdiction_code === profileForm.jurisdictionCode);
      const matterPlans = ensureMatterPlans(practiceAreas, profileForm.matterPlans, profileForm).map((plan) => {
        const selectedPlanCourt = (matterPlanCourtsByState[plan.stateCode || ''] || [])
          .find((court) => court.jurisdiction_code === plan.jurisdictionCode);
        return {
          practice_area: plan.practiceArea,
          title: plan.title?.trim() || plan.practiceArea,
          summary: plan.summary?.trim() || undefined,
          state_code: plan.stateCode || undefined,
          jurisdiction_code: plan.jurisdictionCode || undefined,
          jurisdiction_court_name: selectedPlanCourt?.court_name || plan.jurisdictionCourtName || undefined,
          jurisdiction: selectedPlanCourt?.court_name || plan.jurisdiction || plan.jurisdictionCode || undefined,
          incident_date: plan.incidentDate || undefined,
          incident_location: plan.incidentLocation?.trim() || undefined,
        };
      });
      await intakeCrmApi.updateLeadProfile(lead.clientId, {
        preferred_name: profileForm.preferredName.trim() || undefined,
        email: profileForm.email.trim() || undefined,
        phone: profileForm.phone.trim() || undefined,
        alternate_phone: profileForm.alternatePhone.trim() || undefined,
        home_address: [profileForm.street1, profileForm.street2, profileForm.city, profileForm.state, profileForm.postalCode, profileForm.country].some(Boolean)
          ? {
              street1: profileForm.street1.trim() || undefined,
              street2: profileForm.street2.trim() || undefined,
              city: profileForm.city.trim() || undefined,
              state: profileForm.state.trim() || undefined,
              postalCode: profileForm.postalCode.trim() || undefined,
              country: profileForm.country.trim() || undefined,
            }
          : undefined,
        preferred_contact_method: profileForm.preferredContactMethod.trim() || undefined,
        preferred_contact_time: profileForm.preferredContactTime.trim() || undefined,
        language: profileForm.language.trim() || undefined,
        interpreter_needed: profileForm.interpreterNeeded,
        date_of_birth: profileForm.dateOfBirth || undefined,
        marital_status: profileForm.maritalStatus.trim() || undefined,
        education_level: profileForm.educationLevel.trim() || undefined,
        number_of_dependents: parseOptionalNumber(profileForm.numberOfDependents),
        number_of_kids: parseOptionalNumber(profileForm.numberOfKids),
        employed: profileForm.employed === '' ? undefined : profileForm.employed === 'yes',
        employer_name: profileForm.employerName.trim() || undefined,
        job_title: profileForm.jobTitle.trim() || undefined,
        practice_area: practiceAreas[0] || undefined,
        practice_areas: practiceAreas.length > 0 ? practiceAreas : undefined,
        matter_summary: profileForm.matterSummary.trim() || undefined,
        state_code: profileForm.stateCode || undefined,
        jurisdiction_code: profileForm.jurisdictionCode || undefined,
        jurisdiction_court_name: selectedCourt?.court_name || undefined,
        jurisdiction: selectedCourt?.court_name || profileForm.jurisdictionCode || undefined,
        incident_date: profileForm.incidentDate || undefined,
        incident_location: profileForm.incidentLocation.trim() || undefined,
        opposing_party_names: profileForm.opposingPartyNames.trim() || undefined,
        current_or_prior_attorney: profileForm.currentOrPriorAttorney.trim() || undefined,
        spouse_or_partner_name: profileForm.spouseOrPartnerName.trim() || undefined,
        case_modules: sanitizeLeadCaseModules(profileForm.caseModules, moduleKeys),
        matter_plans: matterPlans,
      });
      setIsEditingProfile(false);
      await onLeadRefresh();
    } catch (err) {
      console.error('[LeadDetailView] Failed to update lead profile:', err);
      setProfileError(err instanceof Error ? err.message : 'Failed to update lead profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const profile = lead.leadProfile || ({} as LeadProfile);
  const completeness = lead.profileCompleteness;
  const aiClassification = lead.aiClassification || {};

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Intake Checklist — spans full width at top */}
      <div className="lg:col-span-2">
        <IntakeChecklist
          lead={lead}
          onToggleItem={onChecklistToggle}
          onRunConflictCheck={onRunConflictCheck}
          onRequestConflictReview={onRequestConflictReview}
          onRecordConflictDisposition={onRecordConflictDisposition}
          onRunExternalConflictEnrichment={onRunExternalConflictEnrichment}
          onOpenConsultationScheduler={() => setConsultationModalOpen(true)}
          onOpenRetainerRequestDesk={() => {
            setRetainerModalMode('request');
            setRetainerModalOpen(true);
          }}
          onOpenRetainerPaymentDesk={() => {
            setRetainerModalMode('payment');
            setRetainerModalOpen(true);
          }}
        />
      </div>

      <div className="lg:col-span-2 bg-gradient-to-r from-white via-blue-50/50 to-emerald-50/40 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Lead Profile Completeness</h3>
              {completeness && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-white/80 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                  {completeness.completeCount}/{completeness.totalCount} complete
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 max-w-2xl">
              Build the baseline identity, household, employment, and matter facts here so the AI synopsis and attorney workflow stay aligned.
            </p>
            {completeness && (
              <div className="w-full max-w-xl">
                <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-lex-accent to-green-500 transition-all"
                    style={{ width: `${completeness.percent}%` }}
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {completeness.percent}% of the recommended lead profile is complete.
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isEditingProfile ? (
              <>
                <button
                  onClick={() => {
                    setProfileForm(createProfileFormState(lead));
                    setIsEditingProfile(false);
                    setProfileError(null);
                  }}
                  className="px-3 py-1.5 text-sm font-medium rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProfileSave}
                  disabled={profileSaving}
                  className="px-3 py-1.5 text-sm font-medium rounded-md bg-lex-accent text-white hover:bg-lex-accent-hover disabled:opacity-50"
                >
                  {profileSaving ? 'Saving...' : 'Save Profile'}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditingProfile(true)}
                className="px-3 py-1.5 text-sm font-medium rounded-md bg-lex-accent text-white hover:bg-lex-accent-hover"
              >
                Edit Profile
              </button>
            )}
          </div>
        </div>
        {completeness && completeness.missingFields.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
              Missing facts
            </p>
            <div className="flex flex-wrap gap-2">
              {completeness.missingFields.map(field => (
                <span key={field} className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                  {field}
                </span>
              ))}
            </div>
          </div>
        )}
        {profileError && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400">{profileError}</p>
        )}
      </div>

      <ScheduleConsultationModal
        lead={lead}
        open={consultationModalOpen}
        onClose={() => setConsultationModalOpen(false)}
        onSubmit={onScheduleConsultation}
      />

      <RetainerDeskModal
        lead={lead}
        open={retainerModalOpen}
        mode={retainerModalMode}
        onClose={() => setRetainerModalOpen(false)}
        onSubmitRequest={onRequestRetainer}
        onSubmitPayment={onRecordRetainerPayment}
      />

      {isEditingProfile && (
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Edit Lead Profile</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ProfileField label="Preferred Name">
              <input value={profileForm.preferredName} onChange={e => updateProfileField('preferredName', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Case Types">
              <input value={profileForm.practiceAreasText} onChange={e => updateProfileField('practiceAreasText', e.target.value)} placeholder="Family Law, Civil Law" className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Primary Email">
              <input type="email" value={profileForm.email} onChange={e => updateProfileField('email', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Primary Phone">
              <input value={profileForm.phone} onChange={e => updateProfileField('phone', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Alternate Phone">
              <input value={profileForm.alternatePhone} onChange={e => updateProfileField('alternatePhone', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Date of Birth">
              <input type="date" value={profileForm.dateOfBirth} onChange={e => updateProfileField('dateOfBirth', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Street Address">
              <input value={profileForm.street1} onChange={e => updateProfileField('street1', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Address Line 2">
              <input value={profileForm.street2} onChange={e => updateProfileField('street2', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="City">
              <input value={profileForm.city} onChange={e => updateProfileField('city', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <div className="grid grid-cols-2 gap-3">
              <ProfileField label="State">
                <input value={profileForm.state} onChange={e => updateProfileField('state', e.target.value)} className={profileInputClassName} />
              </ProfileField>
              <ProfileField label="Postal Code">
                <input value={profileForm.postalCode} onChange={e => updateProfileField('postalCode', e.target.value)} className={profileInputClassName} />
              </ProfileField>
            </div>
            <ProfileField label="Country">
              <input value={profileForm.country} onChange={e => updateProfileField('country', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Preferred Contact Method">
              <input value={profileForm.preferredContactMethod} onChange={e => updateProfileField('preferredContactMethod', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Preferred Contact Time">
              <input value={profileForm.preferredContactTime} onChange={e => updateProfileField('preferredContactTime', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Language">
              <input value={profileForm.language} onChange={e => updateProfileField('language', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Marital Status">
              <input value={profileForm.maritalStatus} onChange={e => updateProfileField('maritalStatus', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Education Level">
              <input value={profileForm.educationLevel} onChange={e => updateProfileField('educationLevel', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Number of Dependents">
              <input type="number" min={0} value={profileForm.numberOfDependents} onChange={e => updateProfileField('numberOfDependents', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Number of Kids">
              <input type="number" min={0} value={profileForm.numberOfKids} onChange={e => updateProfileField('numberOfKids', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Employed">
              <select value={profileForm.employed} onChange={e => updateProfileField('employed', e.target.value as LeadProfileFormState['employed'])} className={profileInputClassName}>
                <option value="">Unknown</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </ProfileField>
            <ProfileField label="Employer Name">
              <input value={profileForm.employerName} onChange={e => updateProfileField('employerName', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Job Title / Occupation">
              <input value={profileForm.jobTitle} onChange={e => updateProfileField('jobTitle', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="State">
              <select value={profileForm.stateCode} onChange={e => {
                updateProfileField('stateCode', e.target.value);
                updateProfileField('jurisdictionCode', '');
              }} className={profileInputClassName}>
                <option value="">Select state</option>
                {jurisdictionStates.map(state => (
                  <option key={state.state_code} value={state.state_code}>
                    {state.state_code} ({state.court_count} courts)
                  </option>
                ))}
              </select>
            </ProfileField>
            <ProfileField label="Court Jurisdiction">
              <select value={profileForm.jurisdictionCode} onChange={e => updateProfileField('jurisdictionCode', e.target.value)} className={profileInputClassName} disabled={!profileForm.stateCode}>
                <option value="">
                  {!profileForm.stateCode ? 'Select state first' : jurisdictionCourts.length === 0 ? 'No courts found' : 'Select court'}
                </option>
                {jurisdictionCourts.map(court => (
                  <option key={court.jurisdiction_code} value={court.jurisdiction_code}>
                    {court.court_name} ({court.jurisdiction_code})
                  </option>
                ))}
              </select>
            </ProfileField>
            <ProfileField label="Incident Date">
              <input type="date" value={profileForm.incidentDate} onChange={e => updateProfileField('incidentDate', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Incident / Dispute Location">
              <input value={profileForm.incidentLocation} onChange={e => updateProfileField('incidentLocation', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Current / Prior Attorney">
              <input value={profileForm.currentOrPriorAttorney} onChange={e => updateProfileField('currentOrPriorAttorney', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Spouse / Partner Name">
              <input value={profileForm.spouseOrPartnerName} onChange={e => updateProfileField('spouseOrPartnerName', e.target.value)} className={profileInputClassName} />
            </ProfileField>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              id="overview_interpreter_needed"
              type="checkbox"
              checked={profileForm.interpreterNeeded}
              onChange={e => updateProfileField('interpreterNeeded', e.target.checked)}
              className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
            />
            <label htmlFor="overview_interpreter_needed" className="text-sm text-gray-700 dark:text-gray-300">
              Interpreter needed
            </label>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3">
            <ProfileField label="Matter Summary">
              <textarea rows={4} value={profileForm.matterSummary} onChange={e => updateProfileField('matterSummary', e.target.value)} className={profileInputClassName} />
            </ProfileField>
            <ProfileField label="Opposing Party Names">
              <textarea rows={3} value={profileForm.opposingPartyNames} onChange={e => updateProfileField('opposingPartyNames', e.target.value)} className={profileInputClassName} />
            </ProfileField>
          </div>
          {selectedModuleKeys.map((moduleKey) => {
            const moduleDefinition = LEAD_CASE_MODULE_DEFINITION_MAP[moduleKey];
            const moduleValues = profileForm.caseModules[moduleKey] || {};
            return (
              <div key={moduleKey} className="mt-5 border-t border-gray-100 dark:border-gray-700 pt-4">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{moduleDefinition.label}</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{moduleDefinition.description}</p>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {moduleDefinition.fields.map((field) => (
                    <ProfileField key={field.key} label={field.label}>
                      {field.input === 'textarea' ? (
                        <textarea
                          rows={field.rows || 3}
                          value={String(moduleValues[field.key] || '')}
                          onChange={e => updateCaseModuleField(moduleKey, field.key, e.target.value)}
                          placeholder={field.placeholder}
                          className={profileInputClassName}
                        />
                      ) : field.input === 'checkbox' ? (
                        <div className="flex items-center gap-2 pt-2">
                          <input
                            id={`overview_${moduleKey}_${field.key}`}
                            type="checkbox"
                            checked={Boolean(moduleValues[field.key])}
                            onChange={e => updateCaseModuleField(moduleKey, field.key, e.target.checked)}
                            className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                          />
                          <label htmlFor={`overview_${moduleKey}_${field.key}`} className="text-sm text-gray-700 dark:text-gray-300">
                            Yes
                          </label>
                        </div>
                      ) : (
                        <input
                          type={field.input}
                          value={field.input === 'number' ? String(moduleValues[field.key] ?? '') : String(moduleValues[field.key] || '')}
                          onChange={e => updateCaseModuleField(
                            moduleKey,
                            field.key,
                            field.input === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value,
                          )}
                          placeholder={field.placeholder}
                          className={profileInputClassName}
                        />
                      )}
                    </ProfileField>
                  ))}
                </div>
              </div>
            );
          })}

          {profileForm.matterPlans.length > 0 && (
            <div className="mt-5 border-t border-gray-100 dark:border-gray-700 pt-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Potential Matters</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Each potential matter can have its own jurisdiction while staying tied to one lead profile.
              </p>
              <div className="mt-3 space-y-4">
                {profileForm.matterPlans.map((plan) => {
                  const planCourts = matterPlanCourtsByState[plan.stateCode || ''] || [];
                  return (
                    <div key={plan.practiceArea} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{plan.practiceArea}</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <ProfileField label="Matter Title">
                          <input
                            value={plan.title || ''}
                            onChange={(e) => updateMatterPlan(plan.practiceArea, { title: e.target.value })}
                            className={profileInputClassName}
                          />
                        </ProfileField>
                        <ProfileField label="State">
                          <select
                            value={plan.stateCode || ''}
                            onChange={(e) => updateMatterPlan(plan.practiceArea, {
                              stateCode: e.target.value,
                              jurisdictionCode: '',
                              jurisdictionCourtName: undefined,
                              jurisdiction: undefined,
                            })}
                            className={profileInputClassName}
                          >
                            <option value="">Select state</option>
                            {jurisdictionStates.map((state) => (
                              <option key={state.state_code} value={state.state_code}>
                                {state.state_code} ({state.court_count} courts)
                              </option>
                            ))}
                          </select>
                        </ProfileField>
                        <ProfileField label="Court Jurisdiction">
                          <select
                            value={plan.jurisdictionCode || ''}
                            onChange={(e) => {
                              const selectedPlanCourt = planCourts.find((court) => court.jurisdiction_code === e.target.value);
                              updateMatterPlan(plan.practiceArea, {
                                jurisdictionCode: e.target.value,
                                jurisdictionCourtName: selectedPlanCourt?.court_name,
                                jurisdiction: selectedPlanCourt?.court_name || e.target.value,
                              });
                            }}
                            className={profileInputClassName}
                            disabled={!plan.stateCode}
                          >
                            <option value="">
                              {!plan.stateCode ? 'Select state first' : planCourts.length === 0 ? 'No courts found' : 'Select court'}
                            </option>
                            {planCourts.map((court) => (
                              <option key={court.jurisdiction_code} value={court.jurisdiction_code}>
                                {court.court_name} ({court.jurisdiction_code})
                              </option>
                            ))}
                          </select>
                        </ProfileField>
                        <ProfileField label="Incident Date">
                          <input
                            type="date"
                            value={plan.incidentDate || ''}
                            onChange={(e) => updateMatterPlan(plan.practiceArea, { incidentDate: e.target.value })}
                            className={profileInputClassName}
                          />
                        </ProfileField>
                        <ProfileField label="Incident / Dispute Location">
                          <input
                            value={plan.incidentLocation || ''}
                            onChange={(e) => updateMatterPlan(plan.practiceArea, { incidentLocation: e.target.value })}
                            className={profileInputClassName}
                          />
                        </ProfileField>
                      </div>
                      <ProfileField label="Matter-Specific Summary">
                        <textarea
                          rows={3}
                          value={plan.summary || ''}
                          onChange={(e) => updateMatterPlan(plan.practiceArea, { summary: e.target.value })}
                          className={profileInputClassName}
                        />
                      </ProfileField>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <InfoCard title="Identity & Contact">
        <FieldRow label="Client Name" value={lead.clientName} />
        <FieldRow label="Preferred Name" value={profile.preferredName} />
        <FieldRow label="Primary Email" value={profile.primaryEmail || lead.email} />
        <FieldRow label="Primary Phone" value={profile.primaryPhone || lead.phone} />
        <FieldRow label="Alternate Phone" value={profile.alternatePhone} />
        <FieldRow label="Date of Birth" value={formatDateValue(profile.dateOfBirth)} />
        <FieldRow label="Contact Attempts" value={String(lead.contactAttempts)} />
        <FieldRow label="Last Contact" value={lead.lastContactDate ? new Date(lead.lastContactDate).toLocaleDateString() : undefined} />
      </InfoCard>

      <InfoCard title="Lead Intelligence">
        {lead.leadScore != null ? (
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold text-white ${
              lead.leadScore >= 70 ? 'bg-green-500' : lead.leadScore >= 40 ? 'bg-yellow-500' : 'bg-red-500'
            }`}>
              {Math.round(lead.leadScore)}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {lead.leadScore >= 70 ? 'Strong Lead' : lead.leadScore >= 40 ? 'Moderate Lead' : 'Needs qualification'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">AI-generated lead score</p>
            </div>
          </div>
        ) : null}
        <FieldRow label="AI Practice Area" value={aiClassification.practiceArea} />
        <FieldRow label="Matter Type" value={aiClassification.matterType} />
        <FieldRow label="Urgency" value={aiClassification.urgencyLevel} />
        <FieldRow label="Confidence" value={aiClassification.confidence != null ? `${aiClassification.confidence}%` : undefined} />
        {lead.tags && lead.tags.length > 0 && (
          <div className="pt-2">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Tags</p>
            <div className="flex flex-wrap gap-1.5">
              {lead.tags.map(tag => (
                <span key={tag} className="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </InfoCard>

      <InfoCard title="Address & Communication">
        <FieldRow label="Home Address" value={formatAddress(profile.homeAddress)} />
        <FieldRow label="Preferred Contact Method" value={profile.preferredContactMethod} />
        <FieldRow label="Preferred Contact Time" value={profile.preferredContactTime} />
        <FieldRow label="Language" value={profile.language} />
        <FieldRow label="Interpreter Needed" value={booleanLabel(profile.interpreterNeeded)} />
        <FieldRow label="Lead Source" value={lead.source} />
      </InfoCard>

      <InfoCard title="Household & Demographics">
        <FieldRow label="Marital Status" value={profile.maritalStatus} />
        <FieldRow label="Education Level" value={profile.educationLevel} />
        <FieldRow label="Number of Dependents" value={numberLabel(profile.numberOfDependents)} />
        <FieldRow label="Number of Kids" value={numberLabel(profile.numberOfKids)} />
        <FieldRow label="Spouse / Partner Name" value={profile.spouseOrPartnerName} />
      </InfoCard>

      <InfoCard title="Employment & Financial Snapshot">
        <FieldRow label="Employed" value={booleanLabel(profile.employed)} />
        <FieldRow label="Employer Name" value={profile.employerName} />
        <FieldRow label="Job Title / Occupation" value={profile.jobTitle} />
      </InfoCard>

      <InfoCard title="Conflict Seeds & Related Parties">
        <FieldRow label="Opposing Party Names" value={profile.opposingPartyNames} multiline />
        <FieldRow label="Current / Prior Attorney" value={profile.currentOrPriorAttorney} />
        <FieldRow label="Employer / Company Seed" value={profile.employerOrCompanyName || profile.employerName} />
      </InfoCard>

      <InfoCard title="Matter Basics" className="lg:col-span-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <FieldRow label="Case Types" value={formatPracticeAreas(profile)} />
          <FieldRow label="Jurisdiction" value={profile.jurisdictionCourtName || profile.jurisdictionCode || profile.jurisdiction} />
          <FieldRow label="Incident / Event Date" value={formatDateValue(profile.incidentDate)} />
          <FieldRow label="Incident / Dispute Location" value={profile.incidentLocation} />
          <FieldRow label="Assigned To" value={lead.assignedToName || lead.assignedToUserId || 'Unassigned'} />
          <FieldRow label="Documents" value={String(lead.documentCount)} />
        </div>
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <FieldRow label="Matter Summary" value={profile.shortSummary || lead.intakeNotes} multiline />
        </div>
      </InfoCard>

      {(profile.matterPlans || []).length > 0 && (
        <InfoCard title="Potential Matters" className="lg:col-span-2">
          <div className="space-y-4">
            {(profile.matterPlans || []).map((matterPlan, index) => (
              <div key={`${matterPlan.practiceArea}-${index}`} className="rounded-lg border border-gray-100 dark:border-gray-700 p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                  <FieldRow label="Matter Title" value={matterPlan.title || matterPlan.practiceArea} />
                  <FieldRow label="Practice Area" value={matterPlan.practiceArea} />
                  <FieldRow label="Jurisdiction" value={matterPlan.jurisdictionCourtName || matterPlan.jurisdictionCode || matterPlan.jurisdiction} />
                  <FieldRow label="State" value={matterPlan.stateCode} />
                  <FieldRow label="Incident Date" value={formatDateValue(matterPlan.incidentDate)} />
                  <FieldRow label="Incident Location" value={matterPlan.incidentLocation} />
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  <FieldRow label="Matter Summary" value={matterPlan.summary} multiline />
                </div>
              </div>
            ))}
          </div>
        </InfoCard>
      )}

      {Object.entries(profile.caseModules || {}).map(([moduleKey, moduleValues]) => {
        const definition = LEAD_CASE_MODULE_DEFINITION_MAP[moduleKey as keyof typeof LEAD_CASE_MODULE_DEFINITION_MAP];
        if (!definition || !moduleValues || Object.keys(moduleValues).length === 0) {
          return null;
        }

        return (
          <InfoCard key={moduleKey} title={definition.label} className="lg:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              {definition.fields.map((field) => (
                <FieldRow
                  key={field.key}
                  label={field.label}
                  value={formatCaseModuleValue(moduleValues[field.key])}
                  multiline={field.input === 'textarea'}
                />
              ))}
            </div>
          </InfoCard>
        );
      })}

      <InfoCard title="Intake Timeline" className="lg:col-span-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <FieldRow label="Created" value={new Date(lead.createdAt).toLocaleString()} />
          <FieldRow label="Updated" value={new Date(lead.updatedAt).toLocaleString()} />
          <FieldRow label="Pipeline Stage" value={lead.intakeFormResponses?.pipeline_stage || lead.status} />
          <FieldRow label="Submission Source" value={lead.intakeFormResponses?.submissionSource || lead.source} />
        </div>
        {lead.intakeFormResponses && Object.keys(lead.intakeFormResponses).length > 0 && (
          <details className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
            <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer">Raw intake response data</summary>
            <div className="bg-gray-50 dark:bg-gray-900 rounded p-3 max-h-56 overflow-auto mt-2">
              {Object.entries(lead.intakeFormResponses).map(([key, value]) => (
                <div key={key} className="flex justify-between py-1 text-xs gap-4">
                  <span className="text-gray-500 dark:text-gray-400 capitalize">{key.replace(/_/g, ' ')}</span>
                  <span className="text-gray-900 dark:text-white ml-2 text-right whitespace-pre-wrap break-all">
                    {typeof value === 'string' ? value : JSON.stringify(value)}
                  </span>
                </div>
              ))}
            </div>
          </details>
        )}
      </InfoCard>
      <ScheduleConsultationModal
        lead={lead}
        open={consultationModalOpen}
        onClose={() => setConsultationModalOpen(false)}
        onSubmit={onScheduleConsultation}
      />
    </div>
  );
}

function InfoCard({ title, children, className = '' }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 ${className}`}>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{title}</h3>
      {children}
    </div>
  );
}

function FieldRow({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value?: string;
  multiline?: boolean;
}) {
  return (
    <div className={`flex ${multiline ? 'flex-col items-start' : 'justify-between'} gap-2 py-1.5`}>
      <dt className="text-sm text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className={`text-sm text-gray-900 dark:text-white ${multiline ? 'whitespace-pre-wrap' : 'text-right'}`}>
        {value || 'Not provided'}
      </dd>
    </div>
  );
}

function ProfileField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</span>
      {children}
    </label>
  );
}

function booleanLabel(value?: boolean): string {
  if (typeof value !== 'boolean') return 'Not provided';
  return value ? 'Yes' : 'No';
}

function numberLabel(value?: number): string {
  return typeof value === 'number' ? String(value) : 'Not provided';
}

function formatDateValue(value?: string): string {
  if (!value) return 'Not provided';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
}

function formatAddress(address?: LeadProfile['homeAddress']): string {
  if (!address) return 'Not provided';
  const parts = [address.street1, address.street2, address.city, address.state, address.postalCode, address.country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : 'Not provided';
}

function formatPracticeAreas(profile: LeadProfile): string {
  if (profile.practiceAreas && profile.practiceAreas.length > 0) {
    return profile.practiceAreas.join(', ');
  }
  return profile.practiceArea || 'Not provided';
}

function formatCaseModuleValue(value: LeadCaseModuleFieldValue | undefined): string {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  return value || 'Not provided';
}

const profileInputClassName = 'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent';

// --- Documents Tab ---

function DocumentsPanel({ documents, clientId }: { documents: LeadDocument[]; clientId: string }) {
  const [generating, setGenerating] = React.useState(false);
  const [loadingTemplates, setLoadingTemplates] = React.useState(true);
  const [templates, setTemplates] = React.useState<IntakeDocumentTemplateOption[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<string>('');
  const [genError, setGenError] = React.useState<string | null>(null);
  const [genSuccess, setGenSuccess] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    const loadTemplates = async () => {
      try {
        setLoadingTemplates(true);
        const availableTemplates = await intakeCrmApi.getDocumentTemplates();
        if (cancelled) return;

        setTemplates(availableTemplates);
        setSelectedTemplateId((current) => current || availableTemplates[0]?.id || '');
      } catch (err) {
        if (!cancelled) {
          setGenError(err instanceof Error ? err.message : 'Failed to load templates');
        }
      } finally {
        if (!cancelled) {
          setLoadingTemplates(false);
        }
      }
    };

    void loadTemplates();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedTemplate = React.useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  const handleGenerateDocument = async () => {
    if (!selectedTemplateId) return;

    setGenerating(true);
    setGenError(null);
    setGenSuccess(null);
    try {
      const result = await intakeCrmApi.generateDocument(clientId, selectedTemplateId);
      setGenSuccess(
        `${result.template_name} ready (${result.variables_resolved.length} variables resolved${result.is_stub_template ? ', placeholder template' : ''}).`
      );
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Failed to generate document');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Document generation button (EF-7G) */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Generate Document</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Auto-fill intake-safe templates with lead data</p>
            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Template
              </label>
              <select
                value={selectedTemplateId}
                onChange={(event) => setSelectedTemplateId(event.target.value)}
                disabled={loadingTemplates || generating || templates.length === 0}
                className="w-full max-w-md px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-lex-accent disabled:opacity-50"
              >
                {templates.length === 0 ? (
                  <option value="">{loadingTemplates ? 'Loading templates...' : 'No templates available'}</option>
                ) : (
                  templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}{template.source === 'stub' ? ' [Stub]' : ''}
                    </option>
                  ))
                )}
              </select>
              {selectedTemplate && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  <p>{selectedTemplate.description}</p>
                  {selectedTemplate.source === 'stub' && (
                    <p className="text-amber-600 dark:text-amber-400">
                      Placeholder template. Swap this out later with the final firm-authored version.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={handleGenerateDocument}
            disabled={generating || loadingTemplates || !selectedTemplateId}
            className="px-3 py-1.5 text-sm font-medium text-lex-accent-contrast bg-lex-accent rounded-md hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
          >
            {generating ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                Generating...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Generate
              </>
            )}
          </button>
        </div>
        {genError && (
          <p className="mt-2 text-xs text-red-600 dark:text-red-400">{genError}</p>
        )}
        {genSuccess && (
          <p className="mt-2 text-xs text-green-600 dark:text-green-400">{genSuccess}</p>
        )}
      </div>

      {/* Document list */}
      {(!documents || documents.length === 0) ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <svg className="mx-auto h-10 w-10 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p>No documents uploaded</p>
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map(doc => (
        <div
          key={doc.documentId}
          className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded bg-lex-accent-soft flex items-center justify-center">
              <svg className="w-4 h-4 text-lex-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{doc.fileName}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {doc.mimeType} &middot; {formatFileSize(doc.fileSize)} &middot; {new Date(doc.uploadedAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <ProcessingBadge status={doc.processingStatus} />
        </div>
      ))}
        </div>
      )}
    </div>
  );
}

function ProcessingBadge({ status }: { status: string }) {
  const configs: Record<string, { bg: string; text: string }> = {
    completed: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300' },
    processing: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300' },
    pending: { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-600 dark:text-gray-400' },
    failed: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300' },
  };
  const c = configs[status?.toLowerCase()] || configs.pending;
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${c.bg} ${c.text}`}>
      {status}
    </span>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

// --- AI Synopsis Tab ---

function SynopsisPanel({ synopsis, lead }: { synopsis: any; lead: LeadDetail }) {
  const intelligence = lead.leadIntelligence;

  if (!synopsis && !intelligence) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        <svg className="mx-auto h-10 w-10 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        <p>AI synopsis not yet generated</p>
        <p className="text-xs mt-1">The synopsis is created after document processing completes.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {intelligence && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Living Lead Intelligence</h3>
          <div className="space-y-3">
            <div>
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Current Summary</h4>
              <p className="text-sm text-gray-900 dark:text-white">{intelligence.summary}</p>
            </div>
            {intelligence.keyFacts?.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Key Facts</h4>
                <ul className="list-disc list-inside text-sm text-gray-900 dark:text-white space-y-1">
                  {intelligence.keyFacts.map((fact, i) => (
                    <li key={i}>{fact}</li>
                  ))}
                </ul>
              </div>
            )}
            {intelligence.potentialMatters?.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Potential Matters</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {intelligence.potentialMatters.map((matter) => (
                    <div key={`${matter.practiceArea}-${matter.title}`} className="rounded border border-gray-200 dark:border-gray-700 p-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{matter.title}</p>
                      <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">{matter.summary}</p>
                      {(matter.jurisdictionCourtName || matter.jurisdictionCode || matter.jurisdiction) && (
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          Jurisdiction: {matter.jurisdictionCourtName || matter.jurisdictionCode || matter.jurisdiction}
                        </p>
                      )}
                      {matter.missingFacts.length > 0 && (
                        <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                          Missing: {matter.missingFacts.join(', ')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {intelligence.recommendedActions?.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Recommended Actions</h4>
                <ul className="list-disc list-inside text-sm text-gray-900 dark:text-white space-y-1">
                  {intelligence.recommendedActions.map((action, i) => (
                    <li key={i}>{action}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
      {synopsis && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">AI-Generated Synopsis</h3>
          {typeof synopsis === 'string' ? (
            <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap">
              {synopsis}
            </div>
          ) : (
            <div className="space-y-3">
              {synopsis.summary && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Summary</h4>
                  <p className="text-sm text-gray-900 dark:text-white">{synopsis.summary}</p>
                </div>
              )}
              {synopsis.key_facts && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Key Facts</h4>
                  <ul className="list-disc list-inside text-sm text-gray-900 dark:text-white space-y-1">
                    {(Array.isArray(synopsis.key_facts) ? synopsis.key_facts : []).map((fact: string, i: number) => (
                      <li key={i}>{fact}</li>
                    ))}
                  </ul>
                </div>
              )}
              {synopsis.recommended_practice_area && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Recommended Practice Area</h4>
                  <p className="text-sm text-gray-900 dark:text-white">{synopsis.recommended_practice_area}</p>
                </div>
              )}
              {synopsis.risk_level && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Risk Level</h4>
                  <p className="text-sm text-gray-900 dark:text-white">{synopsis.risk_level}</p>
                </div>
              )}
              <details className="mt-2">
                <summary className="text-xs text-gray-400 cursor-pointer">Raw synopsis data</summary>
                <pre className="text-xs bg-gray-50 dark:bg-gray-900 rounded p-2 mt-1 overflow-auto max-h-48">
                  {JSON.stringify(synopsis, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Activity Tab ---

function ActivityPanel({ activity }: { activity: LeadActivityEvent[] }) {
  if (!activity || activity.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        <svg className="mx-auto h-10 w-10 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p>No activity recorded</p>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {activity.map((event, idx) => (
        <div key={event.eventId} className="flex gap-3 pb-4">
          {/* Timeline line */}
          <div className="flex flex-col items-center">
            <div className={`w-2.5 h-2.5 rounded-full mt-1.5 ${getEventColor(event.eventType)}`} />
            {idx < activity.length - 1 && (
              <div className="w-px flex-1 bg-gray-200 dark:bg-gray-700 mt-1" />
            )}
          </div>
          {/* Event content */}
          <div className="flex-1 pb-2">
            <p className="text-sm text-gray-900 dark:text-white">{event.description}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {new Date(event.createdAt).toLocaleString()}
              </span>
              <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                {event.source}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function getEventColor(eventType: string): string {
  switch (eventType) {
    case 'stage_change': return 'bg-blue-500';
    case 'contact_attempt': return 'bg-yellow-500';
    case 'note_added': return 'bg-purple-500';
    case 'document_uploaded': return 'bg-green-500';
    case 'ai_analysis': return 'bg-indigo-500';
    default: return 'bg-gray-400';
  }
}

// --- Notes Tab ---

function NotesPanel({
  lead,
  noteText,
  onNoteTextChange,
  onSubmit,
  saving,
}: {
  lead: LeadDetail;
  noteText: string;
  onNoteTextChange: (text: string) => void;
  onSubmit: () => void;
  saving: boolean;
}) {
  const noteEvents = lead.activity.filter(e => e.eventType === 'note_added');

  return (
    <div className="space-y-4">
      {/* Add note form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Add Note</h3>
        <textarea
          value={noteText}
          onChange={e => onNoteTextChange(e.target.value)}
          placeholder="Type a note about this lead..."
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent"
        />
        <div className="flex justify-end mt-2">
          <button
            onClick={onSubmit}
            disabled={!noteText.trim() || saving}
            className="px-3 py-1.5 text-sm font-medium text-lex-accent-contrast bg-lex-accent rounded-md hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : 'Add Note'}
          </button>
        </div>
      </div>

      {/* Existing intake notes */}
      {lead.intakeNotes && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Intake Notes</h3>
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{lead.intakeNotes}</p>
        </div>
      )}

      {/* Note activity timeline */}
      {noteEvents.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Note History</h3>
          <div className="space-y-3">
            {noteEvents.map(event => (
              <div key={event.eventId} className="border-l-2 border-purple-300 dark:border-purple-600 pl-3">
                <p className="text-sm text-gray-900 dark:text-white">{event.description}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {new Date(event.createdAt).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Communication Tab ---

function CommunicationPanel({
  lead,
  contactMethod,
  contactNotes,
  contactOutcome,
  onMethodChange,
  onNotesChange,
  onOutcomeChange,
  onSubmit,
  saving,
}: {
  lead: LeadDetail;
  contactMethod: string;
  contactNotes: string;
  contactOutcome: string;
  onMethodChange: (method: string) => void;
  onNotesChange: (notes: string) => void;
  onOutcomeChange: (outcome: string) => void;
  onSubmit: () => void;
  saving: boolean;
}) {
  const contactEvents = lead.activity.filter(e => e.eventType === 'contact_attempt');

  return (
    <div className="space-y-4">
      {/* Log contact form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Log Contact Attempt</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Method</label>
            <select
              value={contactMethod}
              onChange={e => onMethodChange(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent"
            >
              <option value="phone">Phone</option>
              <option value="email">Email</option>
              <option value="in_person">In Person</option>
              <option value="video_call">Video Call</option>
              <option value="text_message">Text Message</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Outcome</label>
            <select
              value={contactOutcome}
              onChange={e => onOutcomeChange(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent"
            >
              <option value="">Select outcome...</option>
              <option value="connected">Connected</option>
              <option value="voicemail">Left Voicemail</option>
              <option value="no_answer">No Answer</option>
              <option value="scheduled_callback">Scheduled Callback</option>
              <option value="wrong_number">Wrong Number</option>
            </select>
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Notes</label>
          <textarea
            value={contactNotes}
            onChange={e => onNotesChange(e.target.value)}
            placeholder="Contact attempt notes..."
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-lex-accent"
          />
        </div>
        <div className="flex justify-end mt-2">
          <button
            onClick={onSubmit}
            disabled={saving}
            className="px-3 py-1.5 text-sm font-medium text-lex-accent-contrast bg-lex-accent rounded-md hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : 'Log Contact'}
          </button>
        </div>
      </div>

      {/* Contact history */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Contact History ({lead.contactAttempts} attempts)
        </h3>
        {contactEvents.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No contact attempts recorded</p>
        ) : (
          <div className="space-y-3">
            {contactEvents.map(event => (
              <div key={event.eventId} className="flex items-start gap-3 border-l-2 border-yellow-300 dark:border-yellow-600 pl-3">
                <div className="flex-1">
                  <p className="text-sm text-gray-900 dark:text-white">{event.description}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {new Date(event.createdAt).toLocaleString()}
                    {event.source && ` \u2022 ${event.source}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default LeadDetailView;
